/**
 * Auth Store — Gateway-only
 * Simplified from axflow-frontend: no Direct mode, no token refresh
 */
import { proxy } from 'valtio'
import { STORAGE_KEYS } from '@shared/constants'
import { createLoggerWithPrefix } from '@shared/utils/logger'
import type { User } from '@domain/entities'

const logger = createLoggerWithPrefix('authStore')

export interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isInitialized: boolean
  sessionId: string | null
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isInitialized: false,
  sessionId: null,
}

export const authStore = proxy<AuthState>(initialState)

export const authActions = {
  setUser: (user: User | null) => {
    authStore.user = user
    authStore.isAuthenticated = !!user

    if (user) {
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user))
    } else {
      localStorage.removeItem(STORAGE_KEYS.USER)
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
    }
  },

  setToken: (token: string) => {
    authStore.sessionId = token
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token)
  },

  getToken: (): string | null => {
    return authStore.sessionId || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
  },

  logout: () => {
    authStore.user = null
    authStore.isAuthenticated = false
    authStore.sessionId = null
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
    localStorage.removeItem(STORAGE_KEYS.SESSION_ID)
    localStorage.removeItem(STORAGE_KEYS.USER)
  },

  initialize: () => {
    if (authStore.isInitialized) return

    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
    const userStr = localStorage.getItem(STORAGE_KEYS.USER)

    if (token && userStr) {
      try {
        const user = JSON.parse(userStr) as User
        authStore.user = user
        authStore.isAuthenticated = true
        authStore.sessionId = token
      } catch (error) {
        logger.error('Failed to parse stored user data:', error)
        authActions.logout()
      }
    }

    authStore.isInitialized = true
  },
}

export const authSelectors = {
  isAuthenticated: () => authStore.isAuthenticated,
  currentUser: () => authStore.user,
  isDevAdmin: () => authStore.user?.isDevAdmin ?? false,
  isSuperAdmin: () => authStore.user?.isSuperAdmin ?? false,
}
